import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios'
import qs from 'qs'
import localCache from '@/utils/cache'
import {
  finishRequestDiagnostic,
  reportRequestFailure,
  reportResponseStructureSuspect,
  startRequestDiagnostic,
  summarizeResponseShape
} from '@/utils/diagnostics'

export type RequestV2ErrorType =
  | 'business'
  | 'http'
  | 'timeout'
  | 'network'
  | 'cancelled'

interface ApiResponse {
  code: number
  data: unknown
  message?: string
}

export class RequestV2Error extends Error {
  readonly type: RequestV2ErrorType
  readonly status?: number
  readonly businessCode?: number

  constructor(
    message: string,
    type: RequestV2ErrorType,
    options: { status?: number; businessCode?: number } = {}
  ) {
    super(message)
    this.name = 'RequestV2Error'
    this.type = type
    this.status = options.status
    this.businessCode = options.businessCode
  }
}

const isApiResponse = (value: unknown): value is ApiResponse => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false
  const response = value as Partial<ApiResponse>
  return typeof response.code === 'number' && 'data' in response
}

const isSuccessCode = (code: number) => code === 0 || code === 200

const expectsPageResponse = (config: AxiosRequestConfig) => {
  const requestData =
    config.method?.toLowerCase() === 'get' ? config.params : config.data
  const hasPageFields =
    requestData &&
    typeof requestData === 'object' &&
    ('pageNo' in requestData || 'pageSize' in requestData)

  return Boolean(
    hasPageFields || config.url?.toLowerCase().includes('querybycondition')
  )
}

const getHttpErrorMessage = (error: AxiosError) => {
  const responseData = error.response?.data as { message?: unknown } | undefined
  return typeof responseData?.message === 'string'
    ? responseData.message
    : error.message
}

const normalizeRequestError = (error: unknown): RequestV2Error => {
  if (error instanceof RequestV2Error) return error

  if (axios.isCancel(error)) {
    return new RequestV2Error('请求已取消', 'cancelled')
  }

  if (axios.isAxiosError(error)) {
    if (error.code === 'ECONNABORTED') {
      return new RequestV2Error(error.message || '请求超时', 'timeout')
    }
    if (error.response) {
      return new RequestV2Error(getHttpErrorMessage(error), 'http', {
        status: error.response.status
      })
    }
    return new RequestV2Error(error.message || '网络错误', 'network')
  }

  return new RequestV2Error(
    error instanceof Error ? error.message : '请求失败',
    'network'
  )
}

const instance = axios.create({
  baseURL: process.env.VUE_APP_BASE_URL,
  timeout: parseInt(process.env.VUE_APP_TIME_OUT!)
})

instance.interceptors.request.use((config) => {
  const token = localCache.getCache('token')
  config.headers = {
    ...config.headers,
    srcUrl: window.location.hash
  }
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  config.paramsSerializer = (params) =>
    qs.stringify(params, { arrayFormat: 'repeat' })
  return config
})

instance.interceptors.response.use((response: AxiosResponse<unknown>) => {
  const body: unknown = response.data
  if (typeof body === 'object' && body instanceof Blob) return body
  if (!isApiResponse(body)) return body
  if (!isSuccessCode(body.code)) {
    throw new RequestV2Error(body.message || '请求错误', 'business', {
      status: response.status,
      businessCode: body.code
    })
  }
  return body.data
})

class RequestV2 {
  async request<T>(config: AxiosRequestConfig): Promise<T> {
    const context = startRequestDiagnostic({
      method: config.method,
      url: config.url,
      expectsPage: expectsPageResponse(config)
    })

    try {
      const data = await instance.request<unknown, T>(config)
      const responseSummary = summarizeResponseShape(data)
      finishRequestDiagnostic(context, 'success', { ...responseSummary })
      reportResponseStructureSuspect(context, responseSummary)
      return data
    } catch (cause) {
      const error = normalizeRequestError(cause)
      if (error.type === 'business') {
        finishRequestDiagnostic(context, 'business-error', {
          httpStatus: error.status,
          businessCode: error.businessCode
        })
      } else if (error.type === 'cancelled') {
        finishRequestDiagnostic(context, 'cancelled')
      } else {
        reportRequestFailure(
          context,
          cause,
          error.type === 'http' ? 'http-error' : 'request-error'
        )
      }
      throw error
    }
  }

  get<T>(config: AxiosRequestConfig): Promise<T> {
    return this.request<T>({ ...config, method: 'GET' })
  }

  post<T>(config: AxiosRequestConfig): Promise<T> {
    return this.request<T>({ ...config, method: 'POST' })
  }

  put<T>(config: AxiosRequestConfig): Promise<T> {
    return this.request<T>({ ...config, method: 'PUT' })
  }

  delete<T>(config: AxiosRequestConfig): Promise<T> {
    return this.request<T>({ ...config, method: 'DELETE' })
  }
}

export default new RequestV2()
